import type { ErrorReportRequest } from '@crew/shared';
import type { User } from 'firebase/auth';
import { webConfig } from '@/config/env';
import { demoStore } from '@/demo/demoStore';
import { IS_DEMO } from '@/demo/demoMode';

/**
 * דיווח תקלות מהדפדפן ליומן התקלות של המפתח.
 * - נאסף: הודעת השגיאה, פירוט טכני (stack), המסך (בלי פרמטרים ובלי האסימון האישי) והדפדפן.
 * - לא נאסף: תוכן טפסים, שמות, טלפונים.
 * - עד 10 דיווחים לכל טעינת דף, ושגיאה זהה נשלחת פעם אחת בלבד.
 */

let currentUser: User | null = null;
const sent = new Set<string>();
let count = 0;

export function setErrorReporterUser(user: User | null) {
  currentUser = user;
}

export function reportError(err: unknown, extra: Partial<ErrorReportRequest> = {}): void {
  try {
    const e = err instanceof Error ? err : new Error(typeof err === 'string' ? err : 'שגיאה לא ידועה');
    const message = (extra.message ?? e.message ?? '').slice(0, 300);
    const key = `${message}|${(e.stack ?? '').split('\n')[1] ?? ''}`;
    if (sent.has(key) || count >= 10) return;
    sent.add(key);
    count++;
    const path = window.location.pathname;
    const body: ErrorReportRequest = {
      source: extra.source ?? (path.startsWith('/j/') || path.startsWith('/c/') ? 'public' : 'admin'),
      message,
      detail: (extra.detail ?? e.stack ?? '').slice(0, 2000),
      path,
    };
    if (IS_DEMO) return demoStore.reportError(body);
    const base = webConfig.ok ? webConfig.config.apiBaseUrl : '';
    void (async () => {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (currentUser && body.source === 'admin') {
        try {
          headers.Authorization = `Bearer ${await currentUser.getIdToken()}`;
        } catch {
          /* בלי זיהוי */
        }
      }
      await fetch(`${base}/api/errors`, { method: 'POST', headers, body: JSON.stringify(body), keepalive: true }).catch(() => undefined);
    })();
  } catch {
    /* דיווח תקלה לעולם לא מפיל את האפליקציה */
  }
}

let installed = false;

/** מאזין לשגיאות לא מטופלות בכל האפליקציה. נקרא פעם אחת ב-main. */
export function installErrorReporter(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('error', (ev) => {
    // שגיאות טעינת משאבים (תמונה וכו') ותוספים של הדפדפן — לא מעניינות
    if (!ev.error && !ev.message) return;
    if (ev.filename && !ev.filename.startsWith(window.location.origin)) return;
    reportError(ev.error ?? ev.message);
  });
  window.addEventListener('unhandledrejection', (ev) => {
    const reason = ev.reason as { code?: string } | undefined;
    // שגיאות שרת צפויות (קוד שגוי, אין חיבור) כבר מוצגות למשתמש — לא תקלה
    if (reason && typeof reason === 'object' && 'code' in reason && reason.code !== 'internal') return;
    reportError(ev.reason);
  });
}
